import React from 'react';
import { OrderAuditReport, ApprovalStepAudit, ApproverAudit, LineItemAudit, LineItemModificationAudit, OrderAttributeAudit, OrderAttributeChangeAudit } from '../../types';
import { formatCurrency, formatDate } from '../../utils';

interface OrderAuditPrintViewProps {
  auditReport: OrderAuditReport;
}

/**
 * Order Audit Print View Component
 * 
 * Displays a comprehensive audit trail of an order's workflow progression
 * including all approval steps, approvers, decisions, timestamps, and line items.
 * Optimized for printing with proper page breaks and professional formatting.
 */
export function OrderAuditPrintView({ auditReport }: OrderAuditPrintViewProps) {
  const formatDuration = (duration?: string): string => {
    if (!duration) return 'N/A';
    
    // Parse ISO 8601 duration (e.g., "PT2H30M15S" or "P1DT2H")
    const match = duration.match(/PT?(?:(\d+)D)?(?:T)?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);
    if (!match) return duration;
    
    const days = parseInt(match[1] || '0');
    const hours = parseInt(match[2] || '0');
    const minutes = parseInt(match[3] || '0');
    const seconds = parseFloat(match[4] || '0');
    
    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 && days === 0 && hours === 0) parts.push(`${Math.round(seconds)}s`);
    
    return parts.length > 0 ? parts.join(' ') : '< 1m';
  };

  const getStatusBadgeClass = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('completed') || statusLower.includes('approved') || statusLower.includes('accepted')) {
      return 'status-badge-success';
    }
    if (statusLower.includes('rejected') || statusLower.includes('declined')) {
      return 'status-badge-danger';
    }
    if (statusLower.includes('pending')) {
      return 'status-badge-warning';
    }
    if (statusLower.includes('inprogress') || statusLower.includes('in progress')) {
      return 'status-badge-info';
    }
    if (statusLower.includes('notified')) {
      return 'status-badge-info';
    }
    return 'status-badge-default';
  };

  const formatLineItemStatus = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'accepted': return 'Accepted';
      case 'bypassed': return 'Bypassed';
      case 'declined': return 'Declined';
      case 'pending': return 'Pending';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Labels an unchanged-quantity line item action by its actual decision, instead of always assuming "Approved"
  const formatModificationDecision = (decision?: string): string => {
    switch ((decision || '').toLowerCase()) {
      case 'decline': case 'declined': return 'Declined';
      case 'modify': case 'modified': return 'Modified';
      case 'accept': case 'accepted': return 'Approved';
      default: return 'Approved';
    }
  };

  return (
    <div className="order-audit-print-view">
      <style>{`
        .order-audit-print-view {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          max-width: 8.5in;
          margin: 0 auto;
          padding: 0.5in;
          background: white;
          color: #333;
        }

        @media print {
          .order-audit-print-view {
            margin: 0;
            padding: 0.25in;
          }
          
          .page-break {
            page-break-after: always;
          }
          
          .no-break {
            page-break-inside: avoid;
          }
        }

        .audit-header {
          text-align: center;
          margin-bottom: 2rem;
          border-bottom: 3px solid #2563eb;
          padding-bottom: 1rem;
        }

        .audit-header h1 {
          margin: 0 0 0.5rem 0;
          font-size: 1.75rem;
          color: #1e40af;
        }

        .audit-header .order-number {
          font-size: 1.125rem;
          color: #64748b;
          margin: 0;
        }

        .audit-section {
          margin-bottom: 1.5rem;
        }

        .section-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem 1.5rem;
          margin-bottom: 1rem;
        }

        .info-item {
          display: flex;
          flex-direction: column;
        }

        .info-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          margin-bottom: 0.25rem;
        }

        .info-value {
          font-size: 0.95rem;
          color: #1e293b;
        }

        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .status-badge-success {
          background-color: #dcfce7;
          color: #166534;
        }

        .status-badge-danger {
          background-color: #fee2e2;
          color: #991b1b;
        }

        .status-badge-warning {
          background-color: #fef3c7;
          color: #92400e;
        }

        .status-badge-info {
          background-color: #dbeafe;
          color: #1e40af;
        }

        .status-badge-default {
          background-color: #f1f5f9;
          color: #475569;
        }

        .approval-step {
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: #f8fafc;
          border-left: 4px solid #3b82f6;
          border-radius: 0.375rem;
        }

        .step-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .step-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1e293b;
        }

        .approvers-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 0.75rem;
        }

        .approvers-table th {
          background: #e2e8f0;
          padding: 0.5rem;
          text-align: left;
          font-size: 0.875rem;
          font-weight: 600;
          color: #475569;
        }

        .approvers-table td {
          padding: 0.5rem;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.875rem;
        }

        .approvers-table tr:last-child td {
          border-bottom: none;
        }

        .signature-thumbnail {
          max-width: 140px;
          max-height: 50px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          background: #fff;
        }

        .line-items-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 0.75rem;
        }

        .line-items-table th {
          background: #e2e8f0;
          padding: 0.5rem;
          text-align: left;
          font-size: 0.875rem;
          font-weight: 600;
          color: #475569;
        }

        .line-items-table td {
          padding: 0.5rem;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.875rem;
        }

        .line-items-table tr:last-child td {
          border-bottom: none;
        }

        .text-right {
          text-align: right;
        }

        .address-box {
          padding: 0.75rem;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 0.375rem;
          margin-bottom: 1rem;
        }

        .address-box p {
          margin: 0.25rem 0;
          font-size: 0.875rem;
          color: #475569;
        }

        .modification-note {
          background: #fef3c7;
          padding: 0.5rem;
          border-left: 3px solid #f59e0b;
          margin-top: 0.5rem;
          font-size: 0.813rem;
          border-radius: 0.25rem;
        }

        .footer {
          margin-top: 2rem;
          padding-top: 1rem;
          border-top: 2px solid #e2e8f0;
          text-align: center;
          color: #64748b;
          font-size: 0.875rem;
        }

        .empty-state {
          text-align: center;
          padding: 2rem;
          color: #94a3b8;
          font-style: italic;
        }
      `}</style>

      {/* Header */}
      <div className="audit-header no-break">
        <h1>Order Approval Audit Report</h1>
        <p className="order-number">Order #{auditReport.orderNumber}</p>
      </div>

      {/* Order Summary */}
      <div className="audit-section no-break">
        <h2 className="section-title">Order Summary</h2>
        <div className="info-grid">
          <div className="info-item">
            <div className="info-label">Order ID</div>
            <div className="info-value">{auditReport.orderId}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Status</div>
            <div className="info-value">
              <span className={`status-badge ${getStatusBadgeClass(auditReport.status)}`}>
                {auditReport.status}
              </span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">Title</div>
            <div className="info-value">{auditReport.title}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Total Amount</div>
            <div className="info-value">{formatCurrency(auditReport.totalAmount, auditReport.currency)}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Submitted By</div>
            <div className="info-value">{auditReport.submitterName}</div>
          </div>
          {auditReport.submitterEmail && (
            <div className="info-item">
              <div className="info-label">Submitter Email</div>
              <div className="info-value">{auditReport.submitterEmail}</div>
            </div>
          )}
          <div className="info-item">
            <div className="info-label">Division</div>
            <div className="info-value">{auditReport.divisionName || 'N/A'}</div>
          </div>
          {auditReport.referenceNumber && (
            <div className="info-item">
              <div className="info-label">Reference Number</div>
              <div className="info-value">{auditReport.referenceNumber}</div>
            </div>
          )}
          {auditReport.purchaseOrderNumber && (
            <div className="info-item">
              <div className="info-label">PO Number</div>
              <div className="info-value">{auditReport.purchaseOrderNumber}</div>
            </div>
          )}
          {auditReport.shippingMethod && (
            <div className="info-item">
              <div className="info-label">Shipping Method</div>
              <div className="info-value">{auditReport.shippingMethod}</div>
            </div>
          )}
          {auditReport.department && (
            <div className="info-item">
              <div className="info-label">Department</div>
              <div className="info-value">{auditReport.department}</div>
            </div>
          )}
          {auditReport.costCenter && (
            <div className="info-item">
              <div className="info-label">Cost Center</div>
              <div className="info-value">{auditReport.costCenter}</div>
            </div>
          )}
          <div className="info-item">
            <div className="info-label">Created Date</div>
            <div className="info-value">{formatDate(auditReport.createdAt)}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Completed Date</div>
            <div className="info-value">
              {auditReport.completedAt ? formatDate(auditReport.completedAt) : 'In Progress'}
            </div>
          </div>
          {auditReport.processingTime && (
            <div className="info-item">
              <div className="info-label">Processing Time</div>
              <div className="info-value">{formatDuration(auditReport.processingTime)}</div>
            </div>
          )}
          {auditReport.description && (
            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
              <div className="info-label">Description</div>
              <div className="info-value">{auditReport.description}</div>
            </div>
          )}
        </div>
      </div>

      {/* Order Attributes */}
      {auditReport.orderAttributes.length > 0 && (
        <div className="audit-section no-break">
          <h2 className="section-title">Order Attributes</h2>
          <div className="info-grid">
            {auditReport.orderAttributes.map((attr: OrderAttributeAudit, idx: number) => (
              <div className="info-item" key={idx}>
                <div className="info-label">{attr.label}</div>
                <div className="info-value">{attr.value || 'N/A'}</div>
                {attr.changes.length > 0 && (
                  <div className="modification-note">
                    <strong>Change History:</strong>
                    {attr.changes.map((change: OrderAttributeChangeAudit, cIdx: number) => (
                      <div key={cIdx}>
                        • Step {change.step} - {change.modifiedBy}: {change.originalValue || 'N/A'} &rarr; {change.newValue}
                        {change.reason && ` - Reason: ${change.reason}`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Addresses */}
      {(auditReport.shippingAddress || auditReport.billingAddress) && (
        <div className="audit-section no-break">
          <h2 className="section-title">Addresses</h2>
          <div className="info-grid">
            {auditReport.shippingAddress && (
              <div className="info-item">
                <div className="info-label">Shipping Address</div>
                <div className="address-box">
                  {auditReport.shippingAddress.contactName && <p><strong>{auditReport.shippingAddress.contactName}</strong></p>}
                  <p>{auditReport.shippingAddress.street1}</p>
                  {auditReport.shippingAddress.street2 && <p>{auditReport.shippingAddress.street2}</p>}
                  <p>
                    {auditReport.shippingAddress.city}, {auditReport.shippingAddress.state} {auditReport.shippingAddress.zipCode}
                  </p>
                  {auditReport.shippingAddress.country && <p>{auditReport.shippingAddress.country}</p>}
                  {auditReport.shippingAddress.phone && <p>📞 {auditReport.shippingAddress.phone}</p>}
                  {auditReport.shippingAddress.email && <p>✉️ {auditReport.shippingAddress.email}</p>}
                </div>
              </div>
            )}
            {auditReport.billingAddress && (
              <div className="info-item">
                <div className="info-label">Billing Address</div>
                <div className="address-box">
                  {auditReport.billingAddress.contactName && <p><strong>{auditReport.billingAddress.contactName}</strong></p>}
                  <p>{auditReport.billingAddress.street1}</p>
                  {auditReport.billingAddress.street2 && <p>{auditReport.billingAddress.street2}</p>}
                  <p>
                    {auditReport.billingAddress.city}, {auditReport.billingAddress.state} {auditReport.billingAddress.zipCode}
                  </p>
                  {auditReport.billingAddress.country && <p>{auditReport.billingAddress.country}</p>}
                  {auditReport.billingAddress.phone && <p>📞 {auditReport.billingAddress.phone}</p>}
                  {auditReport.billingAddress.email && <p>✉️ {auditReport.billingAddress.email}</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Workflow Information */}
      <div className="audit-section no-break">
        <h2 className="section-title">Workflow Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <div className="info-label">Workflow Name</div>
            <div className="info-value">{auditReport.workflowName}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Total Approval Steps</div>
            <div className="info-value">{auditReport.totalSteps}</div>
          </div>
        </div>
      </div>

      {/* Approval Steps */}
      <div className="audit-section">
        <h2 className="section-title">Approval Workflow Trail</h2>
        
        {auditReport.approvalSteps.length === 0 ? (
          <div className="empty-state">No approval steps found</div>
        ) : (
          auditReport.approvalSteps.map((step: ApprovalStepAudit) => (
            <div key={step.level} className="approval-step no-break">
              <div className="step-header">
                <div className="step-title">
                  Step {step.level}: {step.stepName}
                </div>
                <span className={`status-badge ${getStatusBadgeClass(step.status)}`}>
                  {step.status}
                </span>
              </div>
              
              <div className="info-grid">
                {step.startedAt && (
                  <div className="info-item">
                    <div className="info-label">Started</div>
                    <div className="info-value">{formatDate(step.startedAt)}</div>
                  </div>
                )}
                {step.completedAt && (
                  <div className="info-item">
                    <div className="info-label">Completed</div>
                    <div className="info-value">{formatDate(step.completedAt)}</div>
                  </div>
                )}
                {step.processingTime && (
                  <div className="info-item">
                    <div className="info-label">Processing Time</div>
                    <div className="info-value">{formatDuration(step.processingTime)}</div>
                  </div>
                )}
              </div>

              {step.approvers.length > 0 && (
                <table className="approvers-table">
                  <thead>
                    <tr>
                      <th>{step.stepType === 'notification' ? 'Recipient' : 'Approver'}</th>
                      <th>Email</th>
                      <th>{step.stepType === 'notification' ? 'Action' : 'Decision'}</th>
                      <th>Action Date</th>
                      <th>Response Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {step.approvers.map((approver: ApproverAudit, index: number) => (
                      <React.Fragment key={index}>
                        <tr>
                          <td>{approver.approverName}</td>
                          <td>{approver.approverEmail}</td>
                          <td>
                            <span className={`status-badge ${getStatusBadgeClass(approver.decision)}`}>
                              {approver.decision}
                            </span>
                          </td>
                          <td>{approver.actionDate ? formatDate(approver.actionDate) : 'Pending'}</td>
                          <td>{formatDuration(approver.responseTime)}</td>
                        </tr>
                        {(approver.comments || approver.signatureData) && (
                          <tr>
                            <td colSpan={5}>
                              <div className="modification-note">
                                {approver.comments && (
                                  <div><strong>Notes:</strong> {approver.comments}</div>
                                )}
                                {approver.signatureData && (
                                  <div style={{ marginTop: approver.comments ? '0.5rem' : 0 }}>
                                    <strong>Signature:</strong>
                                    <br />
                                    <img
                                      src={approver.signatureData}
                                      alt={`${approver.approverName} signature`}
                                      className="signature-thumbnail"
                                    />
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))
        )}
      </div>

      {/* Line Items */}
      {auditReport.lineItems.length > 0 && (
        <div className="audit-section page-break">
          <h2 className="section-title">Line Items</h2>
          <table className="line-items-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Item Name</th>
                <th className="text-right">Requested Qty</th>
                <th className="text-right">Approved Qty</th>
                <th className="text-right">Unit Price</th>
                <th className="text-right">Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {auditReport.lineItems.map((item: LineItemAudit) => (
                <React.Fragment key={item.id}>
                  <tr>
                    <td>{item.sku}</td>
                    <td>{item.name}</td>
                    <td className="text-right">
                      {item.requestedQuantity} {item.unitOfMeasure || ''}
                    </td>
                    <td className="text-right">
                      {item.approvedQuantity !== undefined 
                        ? `${item.approvedQuantity} ${item.unitOfMeasure || ''}`
                        : 'N/A'
                      }
                    </td>
                    <td className="text-right">{formatCurrency(item.unitPrice, auditReport.currency)}</td>
                    <td className="text-right">{formatCurrency(item.totalPrice, auditReport.currency)}</td>
                    <td>
                      {item.approvalStatus && (
                        <span className={`status-badge ${getStatusBadgeClass(item.approvalStatus)}`}>
                          {formatLineItemStatus(item.approvalStatus)}
                        </span>
                      )}
                    </td>
                  </tr>
                  {item.modifications.length > 0 && (
                    <tr>
                      <td colSpan={7}>
                        <div className="modification-note">
                          <strong>Approver Actions &amp; Comments:</strong>
                          {item.modifications.map((mod: LineItemModificationAudit, idx: number) => (
                            <div key={idx}>
                              • Level {mod.level} - {mod.approverName}: 
                              {mod.originalQuantity !== mod.modifiedQuantity 
                                ? ` ${mod.originalQuantity} → ${mod.modifiedQuantity}`
                                : ` ${formatModificationDecision(mod.decision)}`}
                              {mod.reason && ` - Reason: ${mod.reason}`}
                              {mod.comments && ` - Comment: ${mod.comments}`}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Notes */}
      {auditReport.notes && (
        <div className="audit-section no-break">
          <h2 className="section-title">Notes</h2>
          <div className="info-value">{auditReport.notes}</div>
        </div>
      )}

      {/* Vendor Information */}
      {auditReport.vendor && (
        <div className="audit-section no-break">
          <h2 className="section-title">Vendor Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-label">Vendor Name</div>
              <div className="info-value">{auditReport.vendor.name}</div>
            </div>
            {auditReport.vendor.email && (
              <div className="info-item">
                <div className="info-label">Contact Email</div>
                <div className="info-value">{auditReport.vendor.email}</div>
              </div>
            )}
            {auditReport.vendor.phone && (
              <div className="info-item">
                <div className="info-label">Contact Phone</div>
                <div className="info-value">{auditReport.vendor.phone}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="footer">
        <p>Generated on {formatDate(new Date().toISOString())}</p>
        <p>This is an official audit report for Order #{auditReport.orderNumber}</p>
      </div>
    </div>
  );
}
